import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Card, Button, Badge, Modal, Form, InputGroup, FormControl } from "react-bootstrap";
import { FaEye, FaPlus, FaTimes, FaSearch } from "react-icons/fa";
import ResponsiveTable from "./ResponsiveTable";
import bonusService from "../services/bonusService";
import organizationalUnitService from "../services/OrganizationalUnitService";
import ProfileImage from "./ProfileImage";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import Select from 'react-select';
import Spinner from "./Spinner";

const BonusRequests = () => {
  // Get current user from context
  const { user } = useContext(AuthContext);
  
  // State
  const [requests, setRequests] = useState([]);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [newRequest, setNewRequest] = useState({
    bonusTypeId: "",
    amount: "",
    reason: ""
  });
  
  // Check if we're on the myBonusRequests page
  const isMyRequests = window.location.pathname.includes('myBonusRequests');

  // Load bonus requests on component mount
  useEffect(() => {
    fetchBonusRequests();
    fetchBonusTypes();
  }, [user, isMyRequests]);

  // Load subordinate users when the create modal is opened
  useEffect(() => {
    if (showCreateModal && user?.userId) {
      fetchSubordinateUsers(user.userId);
      // Reset selected employees when modal opens
      setSelectedEmployees([]);
    }
  }, [showCreateModal, user]);

  // Fetch bonus requests
  const fetchBonusRequests = async () => {
    try {
      setLoading(true);
      let data;
      
      if (isMyRequests) {
        // For myBonusRequests page, fetch only requests for current user
        data = await bonusService.getUserBonusRequests(user.userId);
      } else {
        // For bonusRequests page, fetch all requests the user can manage
        data = await bonusService.getAllBonusRequests();
      }
      
      setRequests(data);
    } catch (error) {
      toast.error("Failed to load bonus requests");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch bonus types for dropdown in create form
  const fetchBonusTypes = async () => {
    try {
      const data = await bonusService.getBonusTypes();
      setBonusTypes(data);
    } catch (error) {
      toast.error("Failed to load bonus types");
      console.error(error);
    }
  };

  // Fetch subordinate users
  const fetchSubordinateUsers = async (userId) => {
    try {
      const data = await organizationalUnitService.getSubordinateUsers(userId);
      // Map the subordinates to format expected by react-select
      const formattedSubordinates = data
        .filter(user => user !== null) // Filter out null users
        .map(user => ({
          value: user.id.toString(),
          label: getUserDisplayName(user),
          imageURL: user.imageURL
        }));
      setSubordinates(formattedSubordinates);
    } catch (error) {
      toast.error("Failed to load subordinate users");
      console.error(error);
    }
  };

  // Get user display name with fallback to username if first/last name is not available
  const getUserDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    } else if (user.username) {
      return user.username;
    } else {
      return `User #${user.id}`;
    }
  };

  // Handle viewing details of a bonus request
  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Handle form input changes for new request
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If changing bonus type, update the default amount if available
    if (name === 'bonusTypeId' && value) {
      const selectedType = bonusTypes.find(type => type.id.toString() === value);
      if (selectedType && selectedType.amount) {
        setNewRequest({
          ...newRequest,
          bonusTypeId: value,
          amount: selectedType.amount
        });
        return;
      }
    }
    
    setNewRequest({
      ...newRequest,
      [name]: value
    });
  };

  // Handle creating a new bonus request(s)
  const handleCreateRequest = async (e) => {
    e.preventDefault();
    
    if (selectedEmployees.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }
    
    if (!newRequest.bonusTypeId || !newRequest.amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      setSubmitting(true);
      
      const promises = selectedEmployees.map(async (employee) => {
        // Create request data
        const requestData = {
          bonusTypeId: parseInt(newRequest.bonusTypeId),
          amount: parseFloat(newRequest.amount),
          reason: newRequest.reason || "",
          userId: parseInt(employee.value)
        };
        
        // Get requesterId from user context and ensure it's a number
        const requesterId = parseInt(user.userId);
        if (isNaN(requesterId)) {
          throw new Error('Invalid requesterId');
        }
        
        console.log('Creating bonus request:', {
          requesterId,
          requestData,
          url: `/BonusRequest/${requesterId}`
        });
        
        // Call the service with requesterId as URL parameter
        const result = await bonusService.createBonusRequest(requesterId, requestData);
        return result;
      });
      
      await Promise.all(promises);
      
      toast.success(`Successfully created ${selectedEmployees.length} bonus request(s)`);
      setShowCreateModal(false);
      setNewRequest({
        bonusTypeId: "",
        amount: "",
        reason: ""
      });
      setSelectedEmployees([]);
      fetchBonusRequests();
    } catch (error) {
      console.error("Error in handleCreateRequest:", error);
      // Show a more user-friendly error message
      toast.error(
        error.message || "Failed to create bonus request(s). Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle canceling a bonus request
  const handleCancelRequest = async (requestId) => {
    try {
      await bonusService.cancelBonusRequest(requestId, user.userId);
      toast.success("Bonus request cancelled successfully");
      fetchBonusRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to cancel bonus request"
      );
    }
  };

  // Status badge color mapping
  const getStatusBadge = (status) => {
    const statusMap = {
      Pending: "warning",
      Approved: "success",
      Rejected: "danger",
      Cancelled: "secondary"
    };
    return <Badge bg={statusMap[status] || "info"}>{status}</Badge>;
  };

  // Table columns configuration
  const columns = [
    { key: "userName", label: "Employee" },
    { key: "requestedByName", label: "Requested By" },
    { key: "bonusTypeName", label: "Bonus Type" },
    { key: "amount", label: "Amount", render: (amount) => `$${amount.toFixed(2)}` },
    { key: "status", label: "Status", render: (status) => getStatusBadge(status) },
    { key: "createdAt", label: "Requested On", render: (date) => new Date(date).toLocaleDateString() }
  ];

  // Define actions based on request status
  const getActions = (request) => {
    const baseActions = [
      {
        label: "View",
        icon: <FaEye />,
        variant: "primary",
        onClick: handleViewDetails
      }
    ];

    // Allow cancellation if:
    // 1. Request is still pending AND
    // 2. Either user created the request OR is the employee receiving the bonus
    if (request.status === "Pending" && 
        (request.requestedByUserId === user.userId || request.userId === user.userId)) {
      baseActions.push({
        label: "Cancel",
        icon: <FaTimes />,
        variant: "danger",
        onClick: () => handleCancelRequest(request.id)
      });
    }

    return baseActions;
  };

  // Custom format option for react-select with profile image
  const formatOptionLabel = ({ label, imageURL }) => (
    <div className="d-flex align-items-center">
      <div className="me-2">
        <ProfileImage 
          imageUrl={imageURL} 
          width={24} 
          height={24} 
          className="rounded-circle"
        />
      </div>
      <div>{label}</div>
    </div>
  );

  // Handle "Select All" functionality
  const handleSelectAll = () => {
    setSelectedEmployees(subordinates);
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
   
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <FaPlus className="me-1" /> New Request
        </Button>
      </div>

      {loading ? (
        <Spinner />
      ) : requests.length === 0 ? (
        <p>No bonus requests found.</p>
      ) : (
        <ResponsiveTable
          data={requests}
          columns={columns}
          getActions={getActions}
        />
      )}

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bonus Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <Row className="mb-3">
                <Col md={2} className="text-center">
                  <div className="mb-2">
                    <ProfileImage 
                      imageUrl={selectedRequest.userImageURL} 
                      width={80} 
                      height={80} 
                      className="rounded-circle"
                    />
                  </div>
                </Col>
                <Col md={5}>
                  <h5>Basic Information</h5>
                  <p><strong>Employee:</strong> {selectedRequest.userName || `User #${selectedRequest.userId}`}</p>
                  <p><strong>Requested By:</strong> {selectedRequest.requestedByName || `User #${selectedRequest.requestedByUserId}`}</p>
                  <p><strong>Bonus Type:</strong> {selectedRequest.bonusTypeName || "Unknown Type"}</p>
                  <p><strong>Amount:</strong> ${selectedRequest.amount.toFixed(2)}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedRequest.status)}</p>
                </Col>
                <Col md={5}>
                  <h5>Dates</h5>
                  <p><strong>Requested On:</strong> {new Date(selectedRequest.createdAt).toLocaleString()}</p>
                  {selectedRequest.updatedAt && (
                    <p><strong>Last Updated:</strong> {new Date(selectedRequest.updatedAt).toLocaleString()}</p>
                  )}
                  {selectedRequest.processedAt && (
                    <p><strong>Processed On:</strong> {new Date(selectedRequest.processedAt).toLocaleString()}</p>
                  )}
                </Col>
              </Row>
              
              <h5>Reason</h5>
              <p>{selectedRequest.reason || "No reason provided"}</p>
              
              {selectedRequest.comments && (
                <>
                  <h5>Reviewer Comments</h5>
                  <p>{selectedRequest.comments}</p>
                  {selectedRequest.processedByName && (
                    <div className="d-flex align-items-center mt-2">
                      <div className="me-2">
                        <ProfileImage 
                          imageUrl={selectedRequest.processedByImageURL} 
                          width={24} 
                          height={24} 
                          className="rounded-circle"
                        />
                      </div>
                      <small>Processed by: {selectedRequest.processedByName}</small>
                    </div>
                  )}
                </>
              )}
              
              {selectedRequest.status === "Pending" && 
               (selectedRequest.requestedByUserId === user.userId || selectedRequest.userId === user.userId) && (
                <div className="mt-3">
                  <Button 
                    variant="danger" 
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleCancelRequest(selectedRequest.id);
                    }}
                  >
                    <FaTimes className="me-1" /> Cancel Request
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Request Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Bonus Request</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateRequest}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">
                  Employees {selectedEmployees.length > 0 && (
                    <Badge bg="primary" className="ms-1">
                      {selectedEmployees.length} selected
                    </Badge>
                  )}
                </Form.Label>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={handleSelectAll}
                  className="p-0"
                >
                  Select All
                </Button>
              </div>
              
              <Select
                isMulti
                options={subordinates}
                value={selectedEmployees}
                onChange={setSelectedEmployees}
                formatOptionLabel={formatOptionLabel}
                placeholder="Select employees who will receive this bonus..."
                className="basic-multi-select"
                classNamePrefix="select"
              />
              
              {selectedEmployees.length === 0 && (
                <div className="text-danger mt-1 small">
                  Please select at least one employee.
                </div>
              )}
              <Form.Text className="text-muted">
                {selectedEmployees.length > 1 
                  ? "Multiple employees selected - a separate bonus request will be created for each employee."
                  : "Select the employees who will receive this bonus."
                }
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Bonus Type</Form.Label>
              <Form.Select
                name="bonusTypeId"
                value={newRequest.bonusTypeId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a bonus type</option>
                {bonusTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} {type.amount ? `(Default: $${type.amount.toFixed(2)})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                name="amount"
                value={newRequest.amount}
                onChange={handleInputChange}
                step="0.01"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="reason"
                value={newRequest.reason}
                onChange={handleInputChange}
                placeholder="Explain the reason for this bonus"
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting || selectedEmployees.length === 0}
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BonusRequests; 